import React, { useState } from 'react';
import { request } from '../Utils/request';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CryptoJS from 'crypto-js';
import { Navigate, useNavigate } from 'react-router-dom';
// Firestore imports
import { handleUserEntryInFirestore } from '../services/CommonApiCall';
import { login } from '../store/slices/AuthSlice';
import { setUser } from '../store/slices/User';
import { useDispatch } from 'react-redux';
import { generateAndRegisterFcmToken } from '../Utils/fcmTokenHelper';

const Signup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [usernameStatus, setUsernameStatus] = useState<string>('');
  const [usernameStatusType, setUsernameStatusType] = useState<'success' | 'error' | ''>('');
  const [isUsernameValid, setIsUsernameValid] = useState<boolean>(false);

  const hashPassword = (password: string, uniqueKey: string): string => {
    const hash = CryptoJS.HmacSHA256(password, uniqueKey);
    return hash.toString();
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
   
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const checkUsername = async (username: string) => {
    console.log(username);
    if (!username) {
      setUsernameStatus('');
      setUsernameStatusType('');
      setIsUsernameValid(false);
      return;
    }
    try {
      const res = await request<any, typeof form>(`/profile/check-username/${username}`, {
        method: 'GET',
      });
      console.log('res', res);
      if (res.status === true ) {
        setUsernameStatus(res.data.message || 'Username is available');
        setUsernameStatusType('success');
        setIsUsernameValid(true);
        console.log('username is available');
      } else {
        setUsernameStatus(res.data.message || 'Username is not available');
        setUsernameStatusType('error');
        setIsUsernameValid(false);
        console.log('username is not available', res.data.message);
      }
    } catch (error: any) {
      setUsernameStatus(error.message || 'Error checking username');
      setUsernameStatusType('error');
      setIsUsernameValid(false);
      console.log('error checking username');
    }
  }

  /**
   * Aligns signup data handling with the login flow:
   * - Hash the password before sending to the API
   * - Persist auth token in the store
   * - Generate and store the FCM token
   * - Hydrate the Redux user slice with the profile data
   * - Ensure Firestore has the latest profile + device token
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    setError(null);
    if (!isUsernameValid) {
      setError('Please choose a valid username before signing up.');
      setLoading(false);
      return;
    }
    try {
      const hashedPassword = hashPassword(form.password, 'ifinds');
      const formWithHashedPassword = { ...form, password: hashedPassword };
      const res = await request<any, typeof form>('/auth/signup', {
        method: 'POST',
        body: formWithHashedPassword,
      });

      let fcmToken: string | null = null;
      if (res.token) {
        dispatch(login(res.token));
        try {
          fcmToken = await generateAndRegisterFcmToken();
        } catch (tokenError) {
          console.warn('Failed to generate FCM token at signup:', tokenError);
        }
      }

      if (res.user?.id) {
        try {
          const profileResponse = await request<any, null>(`/profile/${res.user.id}`, {
            method: 'GET',
          });
          if (profileResponse?.data) {
            dispatch(setUser(profileResponse.data));
          } else if (res.user) {
            dispatch(setUser(res.user));
          }
        } catch (profileError) {
          console.warn('Failed to fetch user profile after signup, falling back to response payload.', profileError);
          if (res.user) {
            dispatch(setUser(res.user));
          }
        }
      }

      // Ensure Firestore has a complete record, including the device token gathered at signup.
      if (res?.success === true && res?.user?.id) {
        const userdataObj = {
          username: res.user?.username,
          email: res.user?.email,
          firstName: res.user?.firstName,
          lastName: res.user?.lastName,
          fcmToken: fcmToken ?? null,
        };
        handleUserEntryInFirestore(res.user.id, userdataObj, fcmToken || '');
      }

      setMessage('Signup successful! Redirecting...');
      navigate('/');
    } catch (error: any) {
      setError(error.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f5f5f5' }}>
      <Paper elevation={3} style={{ padding: 32, minWidth: 350, maxWidth: 400 }}>
        <Typography variant="h5" align="center" gutterBottom>Signup</Typography>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <TextField name="firstName" label="First Name" value={form.firstName} onChange={handleChange} required />
          <TextField name="lastName" label="Last Name" value={form.lastName} onChange={handleChange} required />
          <TextField name="username" label="Username" value={form.username} onChange={handleChange} onBlur={() => checkUsername(form.username)} required />
          {usernameStatus !== '' && (
            <Alert severity={usernameStatusType === 'success' ? 'success' : 'error'} style={{ marginTop: 16 }}>
              {usernameStatus}
            </Alert>
          )}
          <TextField name="email" label="Email" type="email" value={form.email} onChange={handleChange} required />
          <TextField name="password" label="Password" type="password" value={form.password} onChange={handleChange} required />
          <Typography variant="body2" color="text.secondary" style={{ marginTop: 8 }}>{usernameStatusType}</Typography>
          <Button type="submit" variant="contained" color="primary"  style={{ marginTop: 8 }}>
            {loading ? 'Signing up...' : 'Signup'}
          </Button>
        </form>
        {message && <Alert severity="success" style={{ marginTop: 16 }}>{message}</Alert>}
        {error && <Alert severity="error" style={{ marginTop: 16 }}>{error}</Alert>}
      </Paper>
    </div>
  );
};

export default Signup; 